import styled from "@emotion/styled";
import React, { FC, useState } from "react";
import DatePicker from "react-datepicker";
import { useForm } from "react-hook-form";
import CSAButton from "../../components/CSAButton";
import ModalFormInput from "../../components/ModalFormInput";
import ModalFormLabel from "../../components/ModalFormLabel";
import ModalFormTitleInput from "../../components/ModalFormTitleInput";
import parseDate from "../../helpers/parse-date";
import { ImplementationStorageFormData } from "./types";

const Form = styled.form`
  min-width: 32rem;
  min-height: 28rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Submit = styled(CSAButton)`
  margin-top: auto;
  min-width: 8rem;
  align-self: flex-start;
`;

const DatePickerWrapper = styled.div`
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container input {
    width: 100%;
  }
`;

export interface EditImplementationStorageFormProps {
  onSubmit: (implementationStorage: ImplementationStorageFormData) => void;
  implementationStorage?: Partial<ImplementationStorageFormData>;
  readOnly?: boolean;
}

const EditImplementationStorageForm: FC<EditImplementationStorageFormProps> = ({
  onSubmit,
  implementationStorage,
  readOnly = false,
}) => {
  const [dateOfCompletion, setDateOfCompletion] = useState(
    implementationStorage?.dateOfCompletion ?? "",
  );
  const { errors, handleSubmit, register } =
    useForm<ImplementationStorageFormData>();

  return (
    <Form
      onSubmit={handleSubmit((variables) =>
        onSubmit({
          ...variables,
          dateOfCompletion,
        }),
      )}
    >
      <FormSection>
        <ModalFormTitleInput
          ref={register({ required: true })}
          defaultValue={implementationStorage?.name}
          error={!!errors.name}
          name="name"
          placeholder="Implementation Name"
          readOnly={readOnly}
          type="text"
        />

        <ModalFormLabel htmlFor="resourceLink">
          Resource Link
          <ModalFormInput
            ref={register({ required: true })}
            defaultValue={implementationStorage?.resourceLink}
            error={!!errors.resourceLink}
            name="resourceLink"
            placeholder="https://example.com"
            readOnly={readOnly}
            type="url"
          />
        </ModalFormLabel>

        <ModalFormLabel htmlFor="dateOfCompletion">
          Date of Completion
          <DatePickerWrapper>
            <DatePicker
              customInput={<ModalFormInput error={false} />}
              dateFormat="MM-dd-yyyy"
              disabled={readOnly}
              isClearable
              onChange={(d) => {
                if (d) {
                  setDateOfCompletion(d.toISOString());
                  return;
                }

                setDateOfCompletion("");
              }}
              placeholderText="Select completion date"
              popperPlacement="top"
              selected={parseDate(dateOfCompletion)}
            />
          </DatePickerWrapper>
        </ModalFormLabel>
      </FormSection>

      {!readOnly && <Submit type="submit">Save</Submit>}
    </Form>
  );
};

export default EditImplementationStorageForm;
